/**
 * CAD AI ChatBot - Natural Language CAD Control
 * Allows users to design through conversation using LLM
 * Based on CollaborationChat but with AI command execution
 */

class CADChatBot {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.messages = [];
    this.isOpen = false;
    this.currentModel = 'microsoft/DialoGPT-medium';
    this.availableModels = {};
    this.isExecuting = false;

    this.createUI();
    this.setupEventListeners();
    this.loadAvailableModels();

    console.log('[CADChatBot] Initialized');
  }

  createUI() {
    // Create floating AI button (purple theme, stacked with collab chat)
    this.chatButton = document.createElement('button');
    this.chatButton.id = 'cad-ai-button';
    this.chatButton.className = 'chat-button cad-ai-button';
    this.chatButton.title = 'CAD AI Assistant';
    this.chatButton.innerHTML = `
      <img src="static/assets/icons/ai.svg" width="24" height="24" alt="AI Assistant" style="filter: brightness(0) invert(1);">
      <span class="ai-label">AI</span>
    `;
    document.body.appendChild(this.chatButton);

    // Create chat modal (reuse chat-modal structure)
    this.modal = document.createElement('div');
    this.modal.id = 'cad-ai-modal';
    this.modal.className = 'chat-modal cad-ai-modal';
    this.modal.innerHTML = `
      <div class="chat-container">
        <div class="chat-header ai-chat-header">
          <h3>CAD AI Assistant</h3>
          <select id="ai-model-selector" class="ai-model-selector">
            <option value="">Loading models...</option>
          </select>
          <div class="ai-header-controls">
            <button class="ai-control-btn" id="ai-analyze-design" title="Analyze Current Design">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            <button class="ai-control-btn" id="ai-clear-chat" title="Clear Conversation">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
            <button class="chat-close-btn" id="ai-chat-close-btn">&times;</button>
          </div>
        </div>
        <div class="chat-messages" id="ai-chat-messages">
          <div class="ai-welcome-message">
            <img src="static/assets/icons/ai.svg" width="48" height="48" alt="AI Assistant" style="filter: brightness(0) invert(1); opacity: 0.4;">
            <h4>CAD AI Assistant</h4>
            <p>I can help you design with natural language commands.</p>
            <p style="font-size: 12px; color: #888; margin-top: 12px;">
              <strong>I can:</strong><br>
              • Create primitives and sketches<br>
              • Execute operations (extrude, revolve, etc.)<br>
              • Select and modify features<br>
              • Analyze your design<br>
            </p>
          </div>
        </div>
        <div class="llm-quick-actions" id="aiQuickActions">
          <button class="quick-action-btn" onclick="window.sendQuickCADMessage('create a 10mm cube')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18"></rect>
            </svg>
            <span>Create Cube</span>
          </button>
          <button class="quick-action-btn" onclick="window.sendQuickCADMessage('draw a rectangle')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18"></rect>
            </svg>
            <span>Draw Rectangle</span>
          </button>
          <button class="quick-action-btn" onclick="window.sendQuickCADMessage('list features')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
            <span>List Features</span>
          </button>
        </div>
        <div class="chat-input-container">
          <textarea
            id="ai-chat-input"
            class="chat-input"
            placeholder="Ask me to draw, create, or modify... (Enter to send, Shift+Enter for new line)"
            maxlength="1000"
            rows="2"
          ></textarea>
          <button id="ai-send-btn" class="chat-send-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
        <div class="chat-char-count" id="ai-char-count">0 / 1000</div>
      </div>
    `;
    document.body.appendChild(this.modal);

    // Inject AI-specific styles
    this.injectStyles();

    // Setup global quick message function
    window.sendQuickCADMessage = (message) => {
      document.getElementById('ai-chat-input').value = message;
      this.sendCommand();
    };
  }

  setupEventListeners() {
    // Toggle chat
    this.chatButton.addEventListener('click', () => this.toggleChat());

    // Close button
    document.getElementById('ai-chat-close-btn').addEventListener('click', () => this.closeChat());

    // Send button
    document.getElementById('ai-send-btn').addEventListener('click', () => this.sendCommand());

    // Input handling
    const input = document.getElementById('ai-chat-input');
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendCommand();
      }
    });

    // Character count
    input.addEventListener('input', () => {
      this.updateCharCount();
      this.autoResizeInput();
    });

    // Model selector
    document.getElementById('ai-model-selector').addEventListener('change', (e) => {
      this.switchModel(e.target.value);
    });

    // Analyze design button
    document.getElementById('ai-analyze-design').addEventListener('click', () => {
      this.analyzeDesign();
    });

    // Clear chat button
    document.getElementById('ai-clear-chat').addEventListener('click', () => {
      this.clearChat();
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeChat();
      }
    });
  }

  toggleChat() {
    if (this.isOpen) {
      this.closeChat();
    } else {
      this.openChat();
    }
  }

  openChat() {
    this.isOpen = true;
    this.modal.classList.add('open');
    this.chatButton.classList.add('active');

    // Focus input
    setTimeout(() => {
      document.getElementById('ai-chat-input').focus();
    }, 100);

    // Scroll to bottom
    this.scrollToBottom();
  }

  closeChat() {
    this.isOpen = false;
    this.modal.classList.remove('open');
    this.chatButton.classList.remove('active');
  }

  async sendCommand() {
    const input = document.getElementById('ai-chat-input');
    const message = input.value.trim();

    if (!message || this.isExecuting) return;

    // Add user message
    this.addMessage(message, 'user');
    input.value = '';
    this.updateCharCount();

    // Set executing state
    this.isExecuting = true;
    this.showExecuting();

    try {
      // Send to backend for AI parsing
      const token = localStorage.getItem('coandacloud_token');
      const response = await fetch('/api/cad/ai-command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: message,
          model: this.currentModel,
          context: this.getCADContext()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.parsed_command) {
        // Show AI interpretation
        this.addMessage(data.natural_response || 'Executing command...', 'ai');

        // Execute command
        if (window.cadCommandExecutor) {
          const result = await window.cadCommandExecutor.executeCommand(data.parsed_command);

          if (result.success) {
            this.addMessage(`✓ ${result.message}`, 'result-success');
          } else {
            this.addMessage(`✗ ${result.error}`, 'result-error');
          }
        } else {
          throw new Error('Command executor not initialized');
        }
      } else {
        this.addMessage(data.error || 'Could not parse command', 'ai-error');
      }
    } catch (error) {
      console.error('[CADChatBot] Error:', error);
      this.addMessage(`Error: ${error.message}`, 'error');
    } finally {
      this.isExecuting = false;
      this.hideExecuting();
    }
  }

  getCADContext() {
    // Provide FULL CAD state for context-aware and hierarchical commands
    const doc = this.cadViewer.document;
    if (!doc) return { hasDocument: false };

    // Build detailed context with hierarchical entity references
    const sketches = doc.features
      .filter(f => f.type === 'sketch')
      .map((sketch, sketchIndex) => ({
        id: sketch.id,
        name: sketch.name,
        index: sketchIndex + 1,
        entities: (sketch.parameters?.entities || []).map((entity, entityIndex) => ({
          type: entity.type,
          index: entityIndex + 1,
          name: `${entity.type} ${entityIndex + 1}`,  // "rectangle 1", "circle 2"
          id: entity.id || `${sketch.id}-entity-${entityIndex}`
        })),
        entityCount: (sketch.parameters?.entities || []).length
      }));

    const operations = doc.features
      .filter(f => f.type !== 'sketch')
      .map((feature, opIndex) => ({
        id: feature.id,
        name: feature.name,
        type: feature.type,
        index: opIndex + 1,
        baseSketch: feature.parameters?.baseSketch,
        parameters: feature.parameters
      }));

    return {
      hasDocument: true,
      documentName: doc.name,

      // Summary
      featuresCount: doc.features.length,
      partsCount: doc.parts.length,
      sketchesCount: sketches.length,

      // Detailed hierarchical data
      sketches: sketches,
      operations: operations,

      // Current selection
      selectedFeatureId: this.cadViewer.selectedFeatureId,
      selectedFeatureType: this.getSelectedFeatureType(),
      selectedFeatureName: this.getSelectedFeatureName(),

      // History
      canUndo: this.cadViewer.historyManager?.canUndo() || false,
      canRedo: this.cadViewer.historyManager?.canRedo() || false
    };
  }

  getSelectedFeatureName() {
    if (!this.cadViewer.selectedFeatureId) return null;
    const feature = this.cadViewer.document?.getFeature(this.cadViewer.selectedFeatureId);
    return feature?.name || null;
  }

  getSelectedFeatureType() {
    if (!this.cadViewer.selectedFeatureId) return null;
    const feature = this.cadViewer.document?.getFeature(this.cadViewer.selectedFeatureId);
    return feature?.type || null;
  }

  addMessage(content, type) {
    const messageEl = document.createElement('div');
    messageEl.className = `ai-message ai-message-${type}`;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    messageEl.innerHTML = `
      <div class="message-content">${this.escapeHtml(content)}</div>
      <div class="message-time">${timestamp}</div>
    `;

    const messagesContainer = document.getElementById('ai-chat-messages');

    // Remove welcome message if it exists
    const welcomeMsg = messagesContainer.querySelector('.ai-welcome-message');
    if (welcomeMsg) welcomeMsg.remove();

    messagesContainer.appendChild(messageEl);
    this.scrollToBottom();

    // Save to history
    this.messages.push({ content, type, timestamp: Date.now() });
  }

  showExecuting() {
    const messagesContainer = document.getElementById('ai-chat-messages');
    const executingEl = document.createElement('div');
    executingEl.id = 'ai-executing-indicator';
    executingEl.className = 'ai-message ai-message-executing';
    executingEl.innerHTML = `
      <div class="executing-spinner"></div>
      <div class="message-content">Executing command...</div>
    `;
    messagesContainer.appendChild(executingEl);
    this.scrollToBottom();
  }

  hideExecuting() {
    const indicator = document.getElementById('ai-executing-indicator');
    if (indicator) indicator.remove();
  }

  scrollToBottom() {
    const messagesContainer = document.getElementById('ai-chat-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  updateCharCount() {
    const input = document.getElementById('ai-chat-input');
    const counter = document.getElementById('ai-char-count');
    if (input && counter) {
      counter.textContent = `${input.value.length} / 500`;
    }
  }

  autoResizeInput() {
    const input = document.getElementById('ai-chat-input');
    if (input) {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 120) + 'px';
    }
  }

  async loadAvailableModels() {
    try {
      const token = localStorage.getItem('coandacloud_token');
      const response = await fetch('/api/cad/ai-models', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        this.availableModels = data.available_models || {};
        this.currentModel = data.current_model || 'microsoft/DialoGPT-medium';
        this.updateModelSelector();
      }
    } catch (error) {
      console.error('[CADChatBot] Failed to load models:', error);
    }
  }

  updateModelSelector() {
    const selector = document.getElementById('ai-model-selector');
    if (!selector) return;

    selector.innerHTML = Object.entries(this.availableModels).map(([key, model]) => `
      <option value="${key}" ${key === this.currentModel ? 'selected' : ''}>
        ${model.name} (${model.size})
      </option>
    `).join('');
  }

  async switchModel(modelName) {
    if (!modelName || modelName === this.currentModel) return;

    try {
      const token = localStorage.getItem('coandacloud_token');
      const response = await fetch('/api/cad/ai-switch-model', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ model_name: modelName })
      });

      if (response.ok) {
        this.currentModel = modelName;
        this.addMessage(`Switched to ${this.availableModels[modelName]?.name || modelName}`, 'system');
      } else {
        throw new Error('Failed to switch model');
      }
    } catch (error) {
      console.error('[CADChatBot] Model switch failed:', error);
      Utils.notify('Failed to switch model', 'error');
    }
  }

  clearChat() {
    if (confirm('Clear all conversation history?')) {
      this.messages = [];
      const messagesContainer = document.getElementById('ai-chat-messages');
      messagesContainer.innerHTML = `
        <div class="ai-welcome-message">
          <img src="static/assets/icons/ai.svg" width="48" height="48" alt="AI Assistant" style="filter: brightness(0) invert(1); opacity: 0.4;">
          <h4>CAD AI Assistant</h4>
          <p>I can help you design with natural language commands.</p>
          <p style="font-size: 12px; color: #888; margin-top: 12px;">
            <strong>I can:</strong><br>
            • Create primitives and sketches<br>
            • Execute operations (extrude, revolve, etc.)<br>
            • Select and modify features<br>
            • Analyze your design<br>
          </p>
        </div>
      `;
      this.addMessage('Conversation cleared', 'system');
    }
  }

  async analyzeDesign() {
    const doc = this.cadViewer.document;
    if (!doc) {
      Utils.notify('No document to analyze', 'warning');
      return;
    }

    // Build design summary
    const summary = `
Analyzing your CAD design...

**Document:** ${doc.name}
**Features:** ${doc.features.length}
**Parts:** ${doc.parts.length}
**Sketches:** ${doc.features.filter(f => f.type === 'sketch').length}
**Operations:** ${doc.features.filter(f => f.type !== 'sketch').length}

**Feature Breakdown:**
${doc.features.map((f, i) => `${i + 1}. ${f.name} (${f.type})`).join('\n')}

Asking AI for design analysis and suggestions...
    `.trim();

    this.addMessage(summary, 'system');

    // Send to AI for analysis
    const context = this.getCADContext();
    const analysisPrompt = `Analyze this CAD design and provide suggestions for improvement:\n\nDocument: ${doc.name}\nFeatures: ${JSON.stringify(context.sketches)}\nOperations: ${JSON.stringify(context.operations)}`;

    this.isExecuting = true;
    this.showExecuting();

    try {
      const token = localStorage.getItem('coandacloud_token');
      const response = await fetch('/api/cad/ai-command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: analysisPrompt,
          model: this.currentModel,
          context: context
        })
      });

      const data = await response.json();
      this.addMessage(data.natural_response || 'Analysis complete', 'ai');
    } catch (error) {
      this.addMessage(`Analysis failed: ${error.message}`, 'error');
    } finally {
      this.isExecuting = false;
      this.hideExecuting();
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  injectStyles() {
    if (document.getElementById('cad-ai-chat-styles')) return;

    const style = document.createElement('style');
    style.id = 'cad-ai-chat-styles';
    style.textContent = `
      /* CAD AI Button - Purple theme, stacked above collab chat */
      .cad-ai-button {
        position: fixed !important;
        bottom: 96px !important;  /* Stack above collaboration chat */
        right: 24px !important;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%) !important;
        border: none;
        color: white;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        z-index: 9998;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      }

      .cad-ai-button:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 20px rgba(139, 92, 246, 0.6);
      }

      .cad-ai-button.active {
        background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%) !important;
      }

      .cad-ai-button .ai-label {
        position: absolute;
        bottom: -6px;
        right: -6px;
        background: #8B5CF6;
        color: white;
        font-size: 10px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 8px;
        border: 2px solid #1e1e2e;
      }

      /* AI Chat Modal */
      .cad-ai-modal {
        position: fixed;
        bottom: 24px;
        right: 24px;
        display: none;
        z-index: 9999;
        pointer-events: none;
      }

      .cad-ai-modal.open {
        display: block;
      }

      .cad-ai-modal .chat-container {
        pointer-events: auto;
      }

      /* AI Header - Purple theme */
      .ai-chat-header {
        padding: 16px 20px;
        background: linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%) !important;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .ai-chat-header h3 {
        margin: 0;
        color: white;
        font-size: 16px;
        font-weight: 600;
      }

      /* Header Controls */
      .ai-header-controls {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .ai-control-btn {
        background: rgba(255, 255, 255, 0.15);
        border: none;
        color: white;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }

      .ai-control-btn:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      /* Model Selector */
      .ai-model-selector {
        flex: 1;
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 12px;
        cursor: pointer;
        outline: none;
      }

      .ai-model-selector:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      .ai-model-selector option {
        background: #2a2a3e;
        color: white;
      }

      /* AI Messages */
      .ai-message {
        margin-bottom: 12px;
        padding: 10px 14px;
        border-radius: 12px;
        animation: slideIn 0.2s ease;
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .ai-message-user {
        background: #6366F1;
        color: white;
        margin-left: 60px;
        text-align: right;
      }

      .ai-message-ai {
        background: rgba(139, 92, 246, 0.2);
        color: #E0E0E0;
        margin-right: 60px;
      }

      .ai-message-result-success {
        background: rgba(34, 197, 94, 0.2);
        color: #86EFAC;
        border-left: 3px solid #22C55E;
        margin-right: 60px;
      }

      .ai-message-result-error {
        background: rgba(239, 68, 68, 0.2);
        color: #FCA5A5;
        border-left: 3px solid #EF4444;
        margin-right: 60px;
      }

      .ai-message-ai-error {
        background: rgba(251, 146, 60, 0.2);
        color: #FDBA74;
        border-left: 3px solid #FB923C;
        margin-right: 60px;
      }

      .ai-message-system {
        background: rgba(139, 92, 246, 0.1);
        color: #C4B5FD;
        text-align: center;
        font-size: 12px;
        margin: 8px 40px;
      }

      .ai-message-executing {
        background: rgba(139, 92, 246, 0.15);
        color: #C4B5FD;
        margin-right: 60px;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .ai-welcome-message {
        text-align: center;
        padding: 40px 20px;
        color: #888;
      }

      .ai-welcome-message svg {
        margin-bottom: 16px;
      }

      .ai-welcome-message h4 {
        color: #8B5CF6;
        margin: 12px 0 8px 0;
        font-size: 18px;
      }

      .ai-welcome-message p {
        margin: 4px 0;
        font-size: 14px;
      }

      /* Executing Spinner */
      .executing-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(139, 92, 246, 0.3);
        border-top-color: #8B5CF6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .message-time {
        font-size: 10px;
        color: rgba(255, 255, 255, 0.4);
        margin-top: 4px;
      }

      /* Quick Action Buttons */
      .llm-quick-actions {
        display: flex;
        gap: 8px;
        padding: 12px 16px 8px 16px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        flex-wrap: wrap;
      }

      .quick-action-btn {
        flex: 1;
        min-width: 100px;
        padding: 8px 12px;
        background: rgba(139, 92, 246, 0.15);
        border: 1px solid rgba(139, 92, 246, 0.3);
        color: #C4B5FD;
        border-radius: 8px;
        cursor: pointer;
        font-size: 11px;
        font-weight: 500;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
      }

      .quick-action-btn:hover {
        background: rgba(139, 92, 246, 0.3);
        border-color: #8B5CF6;
        transform: translateY(-1px);
      }

      .quick-action-btn span {
        font-size: 14px;
      }

      /* Mobile responsiveness */
      @media (max-width: 768px) {
        .cad-ai-button {
          bottom: 80px !important;
          right: 16px !important;
          width: 48px;
          height: 48px;
        }

        .cad-ai-modal {
          bottom: 0 !important;
          right: 0 !important;
          left: 0 !important;
        }

        .cad-ai-modal .chat-container {
          width: 100% !important;
          max-width: 100% !important;
          height: 100vh !important;
          max-height: 100vh !important;
          border-radius: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADChatBot };
}
